/**
 * Route Configuration
 * Defines all application routes with lazy loading
 */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { PageLoader } from '@/components/common/PageLoader';
import { ProtectedRoute } from './ProtectedRoute';
import { GuestRoute } from './GuestRoute';
import { WorkspaceRoleGuard } from './WorkspaceRoleGuard';
import { EnterpriseAdminRouteGuard } from './EnterpriseAdminRouteGuard';
import { RedirectPreservingSearch } from './RedirectPreservingSearch';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedLayout } from '@/layouts/ProtectedLayout';
import { NotFoundPage } from '@/pages/NotFound';
import { UnauthorizedPage } from '@/pages/Unauthorized';
import { PUBLIC_ROUTES, PROTECTED_ROUTES } from './routes';

/**
 * Retry dynamic imports on failure (stale deployment cache).
 * On chunk load failure, reloads the page once to fetch the new manifest.
 */
function lazyRetry<T extends Record<string, unknown>>(
  importFn: () => Promise<T>,
  key: string
): Promise<T> {
  const storageKey = `chunk-retry-${key}`;
  return importFn().catch((error) => {
    const hasRetried = sessionStorage.getItem(storageKey);
    if (!hasRetried) {
      sessionStorage.setItem(storageKey, '1');
      window.location.reload();
      // Return a never-resolving promise while the page reloads
      return new Promise<T>(() => {});
    }
    sessionStorage.removeItem(storageKey);
    throw error;
  });
}

/**
 * Lazy-loaded page components
 * Code splitting for optimal bundle size
 */

// Auth Pages
const LoginPage = lazy(() =>
  lazyRetry(() => import('@/pages/auth/Login'), 'login').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  lazyRetry(() => import('@/pages/auth/Register'), 'register').then((m) => ({
    default: m.RegisterPage,
  }))
);
const ForgotPasswordPage = lazy(() =>
  lazyRetry(() => import('@/pages/auth/ForgotPassword'), 'forgot-password').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  lazyRetry(() => import('@/pages/auth/ResetPassword'), 'reset-password').then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const ChangePasswordPage = lazy(() =>
  lazyRetry(() => import('@/pages/auth/ChangePassword'), 'change-password').then((m) => ({
    default: m.ChangePasswordPage,
  }))
);
const JoinInvitePage = lazy(() =>
  lazyRetry(() => import('@/pages/workspace/JoinInvite'), 'join-invite').then((m) => ({
    default: m.JoinInvitePage,
  }))
);

// Protected Pages
const DashboardPage = lazy(() =>
  lazyRetry(() => import('@/features/dashboard'), 'dashboard').then((m) => ({
    default: m.DashboardPage,
  }))
);
const ProfilePage = lazy(() =>
  lazyRetry(() => import('@/pages/Profile'), 'profile').then((m) => ({ default: m.ProfilePage }))
);
const SettingsPage = lazy(() =>
  lazyRetry(() => import('@/pages/Settings'), 'settings').then((m) => ({
    default: m.SettingsPage,
  }))
);

// User Management Pages (Admin/Manager only)
const UsersPage = lazy(() =>
  lazyRetry(() => import('@/pages/users/Users'), 'users').then((m) => ({ default: m.UsersPage }))
);
const UserDetailPage = lazy(() =>
  lazyRetry(() => import('@/pages/users/UserDetail'), 'user-detail').then((m) => ({
    default: m.UserDetailPage,
  }))
);
const UserCreatePage = lazy(() =>
  lazyRetry(() => import('@/pages/users/UserCreate'), 'user-create').then((m) => ({
    default: m.UserCreatePage,
  }))
);
const UserEditPage = lazy(() =>
  lazyRetry(() => import('@/pages/users/UserEdit'), 'user-edit').then((m) => ({
    default: m.UserEditPage,
  }))
);

// Workspace Pages
const WorkspaceSettingsPage = lazy(() =>
  lazyRetry(() => import('@/pages/workspace/WorkspaceSettings'), 'workspace-settings').then(
    (m) => ({
      default: m.WorkspaceSettingsPage,
    })
  )
);
const WorkspaceMembersPage = lazy(() =>
  lazyRetry(() => import('@/pages/workspace/WorkspaceMembers'), 'workspace-members').then((m) => ({
    default: m.WorkspaceMembersPage,
  }))
);

// Agents Page (Placeholder)
const AgentsPage = lazy(() =>
  lazyRetry(() => import('@/pages/agents/Agents'), 'agents').then((m) => ({
    default: m.AgentsPage,
  }))
);

// Skill Dashboard Pages (Phase 3.2)
const SkillTargetsPage = lazy(() =>
  lazyRetry(() => import('@/features/skill-dashboard'), 'skill-targets').then((m) => ({
    default: m.SkillTargetsPage,
  }))
);

// Directive Pages (Phase 3.4)
const DirectivesPage = lazy(() =>
  lazyRetry(() => import('@/features/directives'), 'directives').then((m) => ({
    default: m.DirectivesPage,
  }))
);
const DirectiveDetailPage = lazy(() =>
  lazyRetry(() => import('@/features/directives'), 'directive-detail').then((m) => ({
    default: m.DirectiveDetailPage,
  }))
);

// Billing Pages (Phase 4.2 + Phase D)
const BillingPage = lazy(() =>
  lazyRetry(() => import('@/features/billing'), 'billing').then((m) => ({
    default: m.BillingPage,
  }))
);

const PlanSelectionPage = lazy(() =>
  lazyRetry(() => import('@/features/billing'), 'pricing').then((m) => ({
    default: m.PlanSelectionPage,
  }))
);

// Webhook Pages (Phase 4.1)
const WebhookListPage = lazy(() =>
  lazyRetry(() => import('@/features/webhooks'), 'webhook-list').then((m) => ({
    default: m.WebhookListPage,
  }))
);
const WebhookDetailPage = lazy(() =>
  lazyRetry(() => import('@/features/webhooks'), 'webhook-detail').then((m) => ({
    default: m.WebhookDetailPage,
  }))
);

// Security Pages (Phase 3.5.2)
const BestPracticesPage = lazy(() =>
  lazyRetry(() => import('@/features/security'), 'best-practices').then((m) => ({
    default: m.BestPracticesPage,
  }))
);

// Enterprise Admin Pages
const AdminEnterpriseListPage = lazy(() =>
  lazyRetry(() => import('@/features/admin-enterprise'), 'admin-enterprise-list').then((m) => ({
    default: m.AdminEnterpriseListPage,
  }))
);
const AdminEnterpriseDetailPage = lazy(() =>
  lazyRetry(() => import('@/features/admin-enterprise'), 'admin-enterprise-detail').then((m) => ({
    default: m.AdminEnterpriseDetailPage,
  }))
);
const ConnectorGrantsPage = lazy(() =>
  lazyRetry(() => import('@/admin/connector-grants-page'), 'connector-grants').then((m) => ({
    default: m.ConnectorGrantsPage,
  }))
);

// Public Pages (Phase 5.1)
const SkillAnalysisPage = lazy(() =>
  lazyRetry(() => import('@/features/public/pages/SkillAnalysisPage'), 'skill-analysis').then(
    (m) => ({ default: m.SkillAnalysisPage })
  )
);

const SkillComparisonPage = lazy(() =>
  import('@/features/skill-comparison/pages/SkillComparisonPage').then((m) => ({
    default: m.SkillComparisonPage,
  }))
);

// Skill Pages (Phase 3.5)
const SkillsPage = lazy(() =>
  lazyRetry(() => import('@/features/skills/pages/SkillListPage'), 'skills').then((m) => ({
    default: m.SkillListPage,
  }))
);
const SkillDetailPage = lazy(() =>
  lazyRetry(() => import('@/features/skills/pages/SkillDetailPage'), 'skill-detail').then((m) => ({
    default: m.SkillDetailPage,
  }))
);
const SkillManagePage = lazy(() =>
  lazyRetry(() => import('@/features/skills/pages/admin/ManageSkillsPage'), 'skill-manage').then(
    (m) => ({
      default: m.ManageSkillsPage,
    })
  )
);

/**
 * Suspense wrapper for lazy-loaded components
 */
const LazyLoad: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/**
 * Router configuration
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={PROTECTED_ROUTES.DASHBOARD} replace />,
  },
  {
    path: '/billing',
    element: <RedirectPreservingSearch to={PROTECTED_ROUTES.BILLING} />,
  },
  {
    path: '/',
    element: (
      <GuestRoute>
        <AuthLayout />
      </GuestRoute>
    ),
    children: [
      {
        path: PUBLIC_ROUTES.LOGIN,
        element: (
          <LazyLoad>
            <LoginPage />
          </LazyLoad>
        ),
      },
      {
        path: PUBLIC_ROUTES.REGISTER,
        element: (
          <LazyLoad>
            <RegisterPage />
          </LazyLoad>
        ),
      },
      {
        path: PUBLIC_ROUTES.FORGOT_PASSWORD,
        element: (
          <LazyLoad>
            <ForgotPasswordPage />
          </LazyLoad>
        ),
      },
      {
        path: PUBLIC_ROUTES.RESET_PASSWORD,
        element: (
          <LazyLoad>
            <ResetPasswordPage />
          </LazyLoad>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AuthLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: PUBLIC_ROUTES.CHANGE_PASSWORD,
        element: (
          <LazyLoad>
            <ChangePasswordPage />
          </LazyLoad>
        ),
      },
    ],
  },
  {
    path: PUBLIC_ROUTES.JOIN_INVITE,
    element: (
      <LazyLoad>
        <JoinInvitePage />
      </LazyLoad>
    ),
  },
  {
    path: '/join',
    element: (
      <LazyLoad>
        <JoinInvitePage />
      </LazyLoad>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <ProtectedLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/skills/compare',
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <SkillComparisonPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.DASHBOARD,
        element: (
          <LazyLoad>
            <DashboardPage />
          </LazyLoad>
        ),
      },
      {
        path: PROTECTED_ROUTES.PROFILE,
        element: (
          <LazyLoad>
            <ProfilePage />
          </LazyLoad>
        ),
      },
      {
        path: PROTECTED_ROUTES.SETTINGS,
        element: (
          <LazyLoad>
            <SettingsPage />
          </LazyLoad>
        ),
      },
      // User Management Routes (Workspace Admin/Owner only)
      {
        path: PROTECTED_ROUTES.USERS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <UsersPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.USER_DETAIL,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <UserDetailPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.USER_CREATE,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <UserCreatePage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.USER_EDIT,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <UserEditPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      // Workspace Routes (Workspace Admin/Owner only)
      {
        path: PROTECTED_ROUTES.WORKSPACE_SETTINGS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <WorkspaceSettingsPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.WORKSPACE_MEMBERS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <WorkspaceMembersPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.WORKSPACE_INVITES,
        element: <Navigate to={PROTECTED_ROUTES.WORKSPACE_MEMBERS} replace />,
      },
      // Agents Route
      {
        path: PROTECTED_ROUTES.AGENTS,
        element: (
          <LazyLoad>
            <AgentsPage />
          </LazyLoad>
        ),
      },
      // Skill Dashboard Route — redirect to consolidated dashboard
      {
        path: PROTECTED_ROUTES.SKILL_DASHBOARD,
        element: <Navigate to="/dashboard?tab=proficiency" replace />,
      },
      // Skill Targets Route (Workspace Admin/Owner only)
      {
        path: PROTECTED_ROUTES.SKILL_TARGETS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <SkillTargetsPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      // Directive Routes (Phase 3.4)
      {
        path: PROTECTED_ROUTES.DIRECTIVES,
        element: (
          <LazyLoad>
            <DirectivesPage />
          </LazyLoad>
        ),
      },
      {
        path: PROTECTED_ROUTES.DIRECTIVE_DETAIL,
        element: (
          <LazyLoad>
            <DirectiveDetailPage />
          </LazyLoad>
        ),
      },
      // Skill Routes
      {
        path: PROTECTED_ROUTES.SKILLS,
        element: (
          <LazyLoad>
            <SkillsPage />
          </LazyLoad>
        ),
      },
      {
        path: PROTECTED_ROUTES.SKILL_DETAIL,
        element: (
          <LazyLoad>
            <SkillDetailPage />
          </LazyLoad>
        ),
      },
      {
        path: PROTECTED_ROUTES.SKILL_MANAGE,
        element: (
          <WorkspaceRoleGuard fallbackPath="/skills">
            <LazyLoad>
              <SkillManagePage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      // Billing Routes (Workspace Admin/Owner only)
      {
        path: PROTECTED_ROUTES.BILLING,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <BillingPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.PRICING,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <PlanSelectionPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      // Webhook Routes (Workspace Admin/Owner only)
      {
        path: PROTECTED_ROUTES.WEBHOOKS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <WebhookListPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.WEBHOOK_DETAIL,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <WebhookDetailPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
      // Security Best Practices Route (Phase 3.5.2)
      {
        path: PROTECTED_ROUTES.BEST_PRACTICES,
        element: (
          <LazyLoad>
            <BestPracticesPage />
          </LazyLoad>
        ),
      },
      // Enterprise Admin Routes
      {
        path: PROTECTED_ROUTES.ADMIN_ENTERPRISE,
        element: (
          <EnterpriseAdminRouteGuard>
            <LazyLoad>
              <AdminEnterpriseListPage />
            </LazyLoad>
          </EnterpriseAdminRouteGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.ADMIN_ENTERPRISE_DETAIL,
        element: (
          <EnterpriseAdminRouteGuard>
            <LazyLoad>
              <AdminEnterpriseDetailPage />
            </LazyLoad>
          </EnterpriseAdminRouteGuard>
        ),
      },
      {
        path: PROTECTED_ROUTES.CONNECTOR_GRANTS,
        element: (
          <WorkspaceRoleGuard fallbackPath="/dashboard">
            <LazyLoad>
              <ConnectorGrantsPage />
            </LazyLoad>
          </WorkspaceRoleGuard>
        ),
      },
    ],
  },
  // Public Pages (accessible to everyone)
  {
    path: PUBLIC_ROUTES.ANALYZE,
    element: (
      <LazyLoad>
        <SkillAnalysisPage />
      </LazyLoad>
    ),
  },
  // Unauthorized Page
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
  },
  // 404 Not Found
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

/**
 * App Router Component
 * Provides routing to the application
 */
export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
